import { initial } from "lodash";
import { Flex, Icon, Popover } from "orcs-design-system";
import PropTypes from "prop-types";
import React, { Fragment } from "react";
import styled from "styled-components";
import { themeGet } from "@styled-system/theme-get";
import { getGroupNamesArrayFromHierarchy } from "src/allocation/util/group";
import TeamLink from "src/components/TeamLink";
import * as searchUtil from "src/util/search";
import GroupPropType from "../../custom-prop-types/group";

// Plain-text stand-in for TeamLink when the breadcrumb must not navigate
// (e.g. inside a picker in a modal). Mirrors TeamLink's small/bold/noWrap look.
const TeamName = styled.span`
  font-size: ${themeGet("fontSizes.0")};
  font-weight: ${(props) =>
    props.$bold
      ? themeGet("fontWeights.2")(props)
      : themeGet("fontWeights.1")(props)};
  white-space: nowrap;
  color: ${themeGet("colors.bodyText")};
`;

const HierarchyTooltip = ({
  className,
  selectedString,
  group,
  showFullHierarchyInBreadcrumb,
  bold,
  disableLinks = false,
  children,
}) => {
  const { hierarchy } = group;

  const hierarchyTeams = getGroupNamesArrayFromHierarchy(group.hierarchy);
  const parentGroupIds = group.parentIds || initial(group.hierarchyIds); // get all but last if using hierarchyIds

  const breadcrumbGroupIds = showFullHierarchyInBreadcrumb
    ? group.hierarchyIds
    : parentGroupIds;

  if (!hierarchy) {
    return children;
  }
  return (
    <Popover
      direction="top"
      width="min-content"
      text={
        <Flex alignItems="center" flexWrap="wrap" gap="xs">
          {breadcrumbGroupIds.map((breadcrumbGroupId, index) => {
            const teamName = searchUtil.highlightSelectedString(
              hierarchyTeams[index],
              selectedString
            );
            return (
              <Fragment key={breadcrumbGroupId}>
                <Flex alignItems="center" flexWrap="nowrap">
                  {disableLinks ? (
                    <TeamName
                      className={className}
                      $bold={bold}
                      data-testid={`breadcrumb-name-${breadcrumbGroupId}`}
                    >
                      {teamName}
                    </TeamName>
                  ) : (
                    <TeamLink
                      className={className}
                      noWrap
                      small
                      bold={bold}
                      teamName={teamName}
                      team={{ id: breadcrumbGroupId }}
                      data-testid={`breadcrumb-link-${breadcrumbGroupId}`}
                    />
                  )}
                  <Icon
                    ml="xs"
                    icon={["fas", "chevron-right"]}
                    color="mutedText"
                    size="xs"
                  />
                </Flex>
              </Fragment>
            );
          })}
        </Flex>
      }
    >
      {children}
    </Popover>
  );
};

HierarchyTooltip.propTypes = {
  group: GroupPropType.isRequired,
  className: PropTypes.string,
  bold: PropTypes.bool,
  showFullHierarchyInBreadcrumb: PropTypes.bool,
  /** Render breadcrumb team names as plain text instead of links. */
  disableLinks: PropTypes.bool,
  children: PropTypes.element,
  selectedString: PropTypes.string,
};

export default HierarchyTooltip;
